import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Clock, Loader2 } from "lucide-react";
import axios from "axios";
import type { Therapist, TranslateFn, TherapistSlot } from "@/types";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

interface ServiceSelectionFieldsProps {
  serviceType: string;
  durationMinutes: string;
  preferredTherapist: string;
  bookingDatetime: string;
  specialRequests: string;
  selectedPrice: number | null;
  serviceTypes: string[];
  durations: number[];
  therapists: Therapist[];
  onChange: (field: string, value: string) => void;
  t: TranslateFn;
  isNowBooking?: boolean;
}

export const ServiceSelectionFields = ({
  serviceType, durationMinutes, preferredTherapist,
  bookingDatetime, specialRequests, selectedPrice,
  serviceTypes, durations, therapists, onChange, t, isNowBooking,
}: ServiceSelectionFieldsProps) => {
  const [availableSlots, setAvailableSlots] = useState<TherapistSlot[]>([]);
  const [loadingSlots, setLoadingSlots] = useState(false);

  // Fetch available slots when date and duration change
  useEffect(() => {
    if (!bookingDatetime || !durationMinutes) {
      setAvailableSlots([]);
      return;
    }
    const dateStr = bookingDatetime.split("T")[0];
    if (!dateStr) return;

    const fetchSlots = async () => {
      setLoadingSlots(true);
      try {
        const selectedTherapist = preferredTherapist && preferredTherapist !== "none"
          ? therapists.find(th => th.name === preferredTherapist)
          : null;
        const params: Record<string, string> = {
          date: dateStr,
          duration: durationMinutes,
        };
        if (selectedTherapist) {
          params.therapist_id = selectedTherapist.id;
        }
        const res = await axios.get(`${API}/available-slots`, { params });
        setAvailableSlots(res.data.therapists || []);
      } catch {
        setAvailableSlots([]);
      } finally {
        setLoadingSlots(false);
      }
    };
    fetchSlots();
  }, [bookingDatetime, durationMinutes, preferredTherapist, therapists]);

  const handleSlotClick = (slot: string) => {
    const dateStr = bookingDatetime.split("T")[0];
    onChange("booking_datetime", `${dateStr}T${slot}`);
    onChange("is_now_booking", "false");
  };

  const handleNowClick = () => {
    const now = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    const nowStr = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
    onChange("booking_datetime", nowStr);
    onChange("is_now_booking", "true");
  };

  // Get the selected time from bookingDatetime
  const selectedTime = bookingDatetime?.includes("T") ? bookingDatetime.split("T")[1]?.substring(0, 5) : "";

  // Determine which therapists are busy at the selected time (using available slots data)
  const busyTherapistIds = new Set<string>();
  if (selectedTime && availableSlots.length > 0) {
    for (const ts of availableSlots) {
      if (!ts.slots.includes(selectedTime)) {
        busyTherapistIds.add(ts.therapist_id);
      }
    }
  }

  return (
    <div className="space-y-4">
      <h3 className="font-semibold text-lg flex items-center gap-2">
        <Clock className="h-5 w-5" />
        {t('booking.serviceDetails')}
      </h3>

      <div>
        <Label htmlFor="service_type">{t('booking.serviceType')} *</Label>
        <Select value={serviceType} onValueChange={(v) => onChange("service_type", v)}>
          <SelectTrigger data-testid="select-service-type">
            <SelectValue placeholder={t('booking.selectService')} />
          </SelectTrigger>
          <SelectContent position="popper" sideOffset={4}>
            {serviceTypes.map(s => (
              <SelectItem key={s} value={s}>{s}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label htmlFor="duration">{t('booking.duration')} *</Label>
        <Select value={durationMinutes} onValueChange={(v) => onChange("duration_minutes", v)} disabled={!serviceType}>
          <SelectTrigger data-testid="select-duration">
            <SelectValue placeholder={t('booking.selectDuration')} />
          </SelectTrigger>
          <SelectContent position="popper" sideOffset={4}>
            {durations.map(d => (
              <SelectItem key={d} value={d.toString()}>
                {d} {t('common.minutes')}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {selectedPrice && (
        <div className="p-4 bg-primary/10 rounded-lg" data-testid="price-display">
          <p className="text-lg font-semibold text-center">
            {t('booking.totalPrice')}: <span className="text-2xl text-primary">{"\u0E3F"}{selectedPrice}</span>
          </p>
        </div>
      )}

      <div>
        <Label htmlFor="preferred_therapist">{t('booking.preferredTherapist')}</Label>
        <Select value={preferredTherapist} onValueChange={(v) => onChange("preferred_therapist", v)}>
          <SelectTrigger data-testid="select-therapist">
            <SelectValue placeholder={t('booking.anyTherapist')} />
          </SelectTrigger>
          <SelectContent position="popper" sideOffset={4}>
            <SelectItem value="none">{t('booking.anyTherapist')}</SelectItem>
            {therapists.map(th => {
              const isBusy = busyTherapistIds.has(th.id);
              const isUnavailable = th.availability_status === 'unavailable';
              if (isBusy || isUnavailable) return null;
              return (
                <SelectItem key={th.id} value={th.name}>
                  {th.name}
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label htmlFor="booking_datetime">{t('booking.preferredDateTime')}</Label>
        <div className="flex gap-2 mt-1">
          <Input
            id="booking_datetime"
            data-testid="input-datetime"
            type="datetime-local"
            value={bookingDatetime}
            onChange={(e) => { onChange("booking_datetime", e.target.value); onChange("is_now_booking", "false"); }}
            className="flex-1"
          />
          <button
            type="button"
            onClick={handleNowClick}
            className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-bold rounded-md transition-colors shrink-0"
            data-testid="now-btn"
          >
            NOW
          </button>
        </div>
      </div>

      {/* Available Time Slots — hidden when NOW is selected */}
      {bookingDatetime && durationMinutes && !isNowBooking && (
        <div data-testid="available-slots-section">
          <Label className="mb-2 block">Available Time Slots</Label>
          {loadingSlots ? (
            <div className="flex items-center gap-2 text-sm text-gray-500 py-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              Loading available slots...
            </div>
          ) : availableSlots.length === 0 ? (
            <p className="text-sm text-gray-500 py-2">No available slots for this date</p>
          ) : (
            <div className="space-y-3">
              {availableSlots.map((ts) => (
                <div key={ts.therapist_id} className="border rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-medium text-sm">{ts.therapist_name}</span>
                    <span className="text-xs text-gray-400">({ts.working_hours})</span>
                  </div>
                  {ts.slots.length === 0 ? (
                    <p className="text-xs text-gray-400">Fully booked</p>
                  ) : (
                    <div className="flex flex-wrap gap-1.5">
                      {ts.slots.map((slot) => (
                        <Badge
                          key={slot}
                          variant={selectedTime === slot ? "default" : "outline"}
                          className={`cursor-pointer text-xs px-2.5 py-1 transition-colors ${
                            selectedTime === slot 
                              ? "bg-pink-600 text-white hover:bg-pink-700" 
                              : "hover:bg-pink-50 hover:border-pink-300"
                          }`}
                          onClick={() => handleSlotClick(slot)}
                          data-testid={`slot-${ts.therapist_name}-${slot}`}
                        >
                          {slot}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div>
        <Label htmlFor="special_requests">{t('booking.specialRequests')}</Label>
        <textarea
          id="special_requests"
          data-testid="input-special-requests"
          className="w-full min-h-[80px] px-3 py-2 text-sm rounded-md border border-input bg-background"
          value={specialRequests}
          onChange={(e) => onChange("special_requests", e.target.value)}
          placeholder={t('booking.specialPlaceholder')}
        />
      </div>
    </div>
  );
};
